import { UploadOutlined } from "@ant-design/icons";
import {
  Button,
  DatePicker,
  Form,
  Image,
  Input,
  InputNumber,
  message,
  Select,
  Upload,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useId, useState } from "react";
import { TiDeleteOutline } from "react-icons/ti";
import Router from "../..";
import { useUserStore } from "../../../stores/userStore";
import { CATEGORY_MAPPING } from "../../utils/categories";
import {
  CONTENT_FORMAT_OPTIONS,
  parseContentFormatValues,
} from "../../utils/contentFormat";
import {
  buildMissionPayload,
  DEFAULT_MISSION_TITLES,
  isMissionFormFilled,
  parseMissionPayload,
  splitMissionText,
} from "../../utils/mission";
import { Contract } from "./../Contract";

const PANEL = "rounded-2xl bg-[#f8f9fa] border border-gray-100 p-5 md:p-6";

const SECTION_CARD =
  "rounded-2xl border border-gray-100 bg-white p-5 md:p-6 mb-12 " +
  "shadow-[0_1px_3px_rgba(15,23,42,0.08),0_8px_28px_-4px_rgba(15,23,42,0.12)]";

const THUMBNAIL_WIDTH = 1200;
const THUMBNAIL_HEIGHT = 630;
const THUMBNAIL_ASPECT = THUMBNAIL_WIDTH / THUMBNAIL_HEIGHT;
const PAYOUT_STEP = 50000;
const PAYOUT_MAX = 3000000;
const payoutFormatter = new Intl.NumberFormat("ko-KR");
const payoutOptions = Array.from(
  { length: PAYOUT_MAX / PAYOUT_STEP },
  (_, index) => {
    const value = (index + 1) * PAYOUT_STEP;
    return {
      value: String(value),
      label: `${payoutFormatter.format(value)}원`,
    };
  }
);
const toPayoutSelectValue = (value) =>
  value === null || value === undefined || value === "" ? value : String(value);
const toPayoutSubmitValue = (value) =>
  value === null || value === undefined || value === "" ? value : Number(value);

const normalizeMissionHashtag = (raw) => {
  const t = raw.trim();
  if (!t || t === "#") return "";
  return t.startsWith("#") ? t : `#${t}`;
};

const normalizeMissionAccountTag = (raw) => {
  const t = raw.trim();
  if (!t || t === "@") return "";
  return t.startsWith("@") ? t : `@${t}`;
};

const MissionTagInput = ({
  value,
  onChange,
  disabled,
  placeholder,
  normalizeTag = (w) => w,
}) => {
  const [draft, setDraft] = useState("");
  const list = Array.isArray(value) ? value : [];

  const commit = () => {
    const words = splitMissionText(draft).map(normalizeTag).filter(Boolean);
    if (words.length === 0) return;

    const next = [...list];
    for (const word of words) {
      if (!next.includes(word)) next.push(word);
    }
    onChange?.(next);
    setDraft("");
  };

  const removeAt = (index) => {
    const next = list.filter((_, i) => i !== index);
    onChange?.(next);
  };

  return (
    <div className="min-w-0">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== "Enter") return;
            if (e.nativeEvent.isComposing || e.keyCode === 229) return;
            e.preventDefault();
            e.stopPropagation();
            commit();
          }}
          disabled={disabled}
          placeholder={placeholder}
          className="!rounded-xl border-gray-200 text-[15px] sm:flex-1"
        />
      </div>
      {list.length > 0 && (
        <ul className="m-0 mt-3 flex list-none flex-wrap gap-2 p-0">
          {list.map((tag, i) => (
            <li
              key={`${i}-${tag}`}
              className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-gray-200 bg-white py-1 pl-3 pr-1 text-[15px] text-gray-800 shadow-sm"
            >
              <span className="min-w-0 truncate" title={tag}>
                {tag}
              </span>
              {!disabled && (
                <button
                  type="button"
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
                  onClick={() => removeAt(i)}
                  aria-label={`${tag} 삭제`}
                >
                  <TiDeleteOutline className="text-lg" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const FormatSelector = ({ value, onChange, disabled, allowedValues }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
    {CONTENT_FORMAT_OPTIONS.map((opt) => {
      const selectedValues = parseContentFormatValues(value);
      const active = selectedValues.includes(opt.value);
      const isAllowed =
        !Array.isArray(allowedValues) || allowedValues.includes(opt.value);
      const isDisabled = disabled || !isAllowed;
      const Icon = opt.Icon;
      return (
        <button
          key={opt.value}
          type="button"
          disabled={isDisabled}
          onClick={() => {
            if (!isAllowed) return;
            if (selectedValues.includes(opt.value)) {
              onChange?.(selectedValues.filter((v) => v !== opt.value));
            } else {
              onChange?.([...selectedValues, opt.value]);
            }
          }}
          className={[
            "relative flex items-center justify-center gap-2 rounded-2xl border px-3 py-3 text-center transition-all",
            active
              ? "border-[#0d6efd] bg-[#e7f1ff] text-[#0a58ca] shadow-sm"
              : isAllowed
                ? "border-gray-200 bg-white text-gray-800 hover:border-blue-100 hover:bg-blue-50"
                : "border-gray-200 bg-gray-100 text-gray-400",
            isDisabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
          ].join(" ")}
        >
          {Icon ? <Icon /> : null}
          <span className="text-base md:text-xl font-semibold leading-tight whitespace-nowrap overflow-hidden">
            {opt.shortLabel}
          </span>
        </button>
      );
    })}
  </div>
);

const BinaryRadios = ({ value, onChange, disabled, options }) => {
  const groupName = useId();
  return (
    <div className="grid w-full gap-2 grid-cols-2" role="radiogroup">
      {options.map((opt) => {
        const selected = value === opt.value;
        return (
          <label
            key={String(opt.value)}
            className={[
              "flex cursor-pointer items-center justify-center whitespace-nowrap text-ellipsis overflow-hidden rounded-xl border px-3 py-2.5 text-center font-semibold transition-colors text-xl",
              selected
                ? "border-[#0d6efd] bg-[#e7f1ff] text-[#0a58ca] shadow-sm"
                : "border-gray-200 bg-white text-gray-800 hover:border-blue-100 hover:bg-blue-50",
              disabled ? "cursor-not-allowed opacity-50" : "",
            ].join(" ")}
          >
            <input
              type="radio"
              className="sr-only"
              name={groupName}
              checked={selected}
              disabled={disabled}
              onChange={() => onChange?.(opt.value)}
            />
            {opt.label}
          </label>
        );
      })}
    </div>
  );
};

const SectionHeader = ({ title, description }) => (
  <div className="mb-4">
    <h3 className="text-2xl font-bold tracking-tight text-gray-900">{title}</h3>
    {description && (
      <p className="mt-1.5 text-xl text-gray-500">{description}</p>
    )}
  </div>
);

const scrollToId = (id) => {
  const el = document.getElementById(id);
  el?.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const CampaignForm = ({
  form: formProp,
  onFinish,
  mode = "create",
  initialValues,
  hiddenFields = [],
  disabledFields = [],
  campaignStatus,
  onThumbnailChange,
  onAttachmentChange,
}) => {
  const [defaultForm] = Form.useForm();
  const form = formProp || defaultForm;
  const { userInfo } = useUserStore();
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [cropImageUrl, setCropImageUrl] = useState(null);
  const [pendingThumbnailFile, setPendingThumbnailFile] = useState(null);

  const [activeNav, setActiveNav] = useState("cf-section-basic");
  const formValues = Form.useWatch([], form);
  const [companyMembers, setCompanyMembers] = useState([]);
  const contentFormatAllowedValues = ["tiktok_video", "tiktok_feed"];

  const showSidebar = mode === "create";
  const missionInitialValues = parseMissionPayload(initialValues?.mission);

  const mergedInitialValues = {
    ...initialValues,
    payout: toPayoutSelectValue(initialValues?.payout),
    content_format: parseContentFormatValues(initialValues?.content_format),
    mission_guide:
      initialValues?.mission_guide ?? missionInitialValues.mission_guide,
    mission_hashtags:
      initialValues?.mission_hashtags ?? missionInitialValues.mission_hashtags,
    mission_account_tags:
      initialValues?.mission_account_tags ??
      missionInitialValues.mission_account_tags,
    mission_keywords:
      initialValues?.mission_keywords ?? missionInitialValues.mission_keywords,
    mission_custom_sections:
      initialValues?.mission_custom_sections ??
      missionInitialValues.mission_custom_sections,
  };

  const navItems = [
    {
      id: "cf-section-basic",
      title: "기본 정보",
      sub: "이름 · 모집 · 일정 · 소개",
    },
    {
      id: "cf-section-media",
      title: "이미지",
      sub: "썸네일 · 상세 페이지 첨부 파일",
    },
    {
      id: "cf-section-format",
      title: "콘텐츠 형식",
      sub: "플랫폼 선택",
    },
    {
      id: "cf-section-ops",
      title: "운영 설정",
      sub: "시딩 · 배송 · 2차 가공 · 유가 시 통화·리워드",
    },
    {
      id: "cf-section-mission",
      title: "미션",
      sub: "미션 상세 내용",
    },
    {
      id: "cf-section-legal",
      title: "약관 동의",
      sub: "계약 동의",
    },
  ];

  const hasText = (value) =>
    typeof value === "string" && value.trim().length > 0;
  const hasSelectedContentFormat =
    parseContentFormatValues(formValues?.content_format).length > 0;
  const hasMission = isMissionFormFilled(formValues);
  const hasMedia =
    Boolean(thumbnailFile) ||
    Boolean(attachmentFile) ||
    Boolean(initialValues?.thumbnail) ||
    Boolean(initialValues?.attachment_url);

  const sectionStatuses = {
    "cf-section-basic":
      hasText(formValues?.title) &&
      Boolean(formValues?.category) &&
      Boolean(formValues?.recruitment_count) &&
      Boolean(formValues?.start_date) &&
      Boolean(formValues?.end_date) &&
      hasText(formValues?.description),
    "cf-section-format": hasSelectedContentFormat,
    "cf-section-ops":
      formValues?.seeding_type !== undefined &&
      formValues?.requires_shipment !== undefined &&
      formValues?.allow_secondary_use !== undefined &&
      (formValues?.seeding_type !== "paid" ||
        (Boolean(formValues?.currency) &&
          Number(formValues?.payout) > 0 &&
          Number(formValues?.payout) <= PAYOUT_MAX &&
          Number(formValues?.payout) % PAYOUT_STEP === 0)),
    "cf-section-mission": hasMission,
    "cf-section-media": hasMedia,
    "cf-section-legal":
      mode !== "create" || Boolean(formValues?.contractData?.agreed),
  };

  const startDatePassed =
    campaignStatus === "active" && initialValues?.start_date
      ? dayjs().isAfter(dayjs(initialValues.start_date), "day")
      : false;

  const fetchMembers = async () => {
    const res = await Router.Api.getMembers();
    setCompanyMembers(res?.result ?? []);
  };

  // 캠페인 생성 모드일 때 계약자와 회사명 자동 채우기
  useEffect(() => {
    if (mode === "create" && userInfo) {
      const username =
        userInfo?.user_info?.first_name && userInfo?.user_info?.last_name
          ? `${userInfo.user_info.first_name} ${userInfo.user_info.last_name}`
          : "";
      const companyName = userInfo?.type_info?.company_name || "";
      if (username || companyName) {
        form.setFieldsValue({
          username,
          company_name: companyName,
        });
      }
    }
    if (mode === "edit") {
      fetchMembers();
    }
  }, [mode, userInfo, form]);

  const isFormValid = () => {
    if (!formValues?.title?.trim()) return false;
    if (!formValues?.description?.trim()) return false;
    if (!hasSelectedContentFormat) return false;
    if (!isMissionFormFilled(formValues)) return false;
    if (!formValues?.category) return false;
    if (formValues?.seeding_type === undefined) return false;
    if (!formValues?.recruitment_count) return false;
    if (formValues?.seeding_type === "paid") {
      const payoutNum = Number(formValues?.payout);
      if (
        !formValues?.currency ||
        !(
          payoutNum > 0 &&
          payoutNum <= PAYOUT_MAX &&
          payoutNum % PAYOUT_STEP === 0
        )
      )
        return false;
    }
    if (formValues?.requires_shipment === undefined) return false;
    if (formValues?.allow_secondary_use === undefined) return false;
    if (!formValues?.start_date) return false;
    if (!formValues?.end_date) return false;
    if (mode === "create" && !formValues?.contractData?.agreed) return false;
    return true;
  };

  const missionDisabled = disabledFields.includes("mission");

  const resetCropState = () => {
    if (cropImageUrl) {
      URL.revokeObjectURL(cropImageUrl);
    }
    setCropModalOpen(false);
    setCropImageUrl(null);
    setPendingThumbnailFile(null);
  };

  const handleThumbnailBeforeUpload = (file) => {
    const maxBytes = 30 * 1024 * 1024;
    if (file.size > maxBytes) {
      message.error("파일 크기는 30MB 이하여야 합니다.");
      return Upload.LIST_IGNORE;
    }

    if (cropImageUrl) {
      URL.revokeObjectURL(cropImageUrl);
    }

    setPendingThumbnailFile(file);
    setCropImageUrl(URL.createObjectURL(file));
    setCropModalOpen(true);
    return false;
  };

  const formInner = (
    <Form
      form={form}
      layout="vertical"
      onFinish={(vals) => {
        onFinish?.({
          ...vals,
          payout: toPayoutSubmitValue(vals.payout),
          mission: buildMissionPayload(vals),
        });
      }}
      initialValues={mergedInitialValues}
      requiredMark={false}
    >
      {mode === "edit" && (
        <section className="scroll-mt-28 mb-6 md:mb-8">
          <div className={SECTION_CARD}>
            <SectionHeader title="담당자" />
            <Form.Item
              name="manager_user_id"
              rules={[
                {
                  required: true,
                  message: "담당자를 선택해 주세요",
                },
              ]}
              className="mb-0"
            >
              <Select
                placeholder="담당자를 선택해 주세요"
                disabled={disabledFields.includes("manager_user_id")}
                className="!rounded-xl border-gray-200"
                options={companyMembers.map((member) => ({
                  value: member.id,
                  label: `${member.first_name} ${member.last_name}`,
                }))}
              />
            </Form.Item>
          </div>
        </section>
      )}

      {(!hiddenFields.includes("title") ||
        !hiddenFields.includes("category") ||
        !hiddenFields.includes("recruitment_count") ||
        !hiddenFields.includes("start_date") ||
        !hiddenFields.includes("end_date") ||
        !hiddenFields.includes("description") ||
        !hiddenFields.includes("product_info")) && (
        <section id="cf-section-basic" className="scroll-mt-28 mb-6 md:mb-8">
          <div className={SECTION_CARD}>
            <SectionHeader
              title="캠페인 기본 정보"
              description={
                <>
                  <p>
                    캠페인명·카테고리·모집 인원·일정·한 줄 소개·제공 제품 정보를
                    입력해 주세요.
                  </p>
                  <p>
                    캠페인 승인 과정에서 노출되는 안내 문구가 수정·보완될 수
                    있으며, 필요 시 별도로 안내드립니다.
                  </p>
                </>
              }
            />
            <div className={PANEL}>
              <div className="flex flex-col gap-4">
                {(!hiddenFields.includes("title") ||
                  !hiddenFields.includes("category")) && (
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 sm:items-start">
                    {!hiddenFields.includes("title") && (
                      <Form.Item
                        name="title"
                        label={<span className="font-semibold">캠페인명</span>}
                        rules={[
                          {
                            required: true,
                            message: "캠페인명을 입력해주세요",
                          },
                        ]}
                        className="mb-0 min-w-0 sm:col-span-2"
                      >
                        <Input
                          placeholder="캠페인명을 입력해주세요"
                          disabled={disabledFields.includes("title")}
                          className="!rounded-xl border-gray-200"
                        />
                      </Form.Item>
                    )}
                    {!hiddenFields.includes("category") && (
                      <Form.Item
                        name="category"
                        label={<span className="font-semibold">카테고리</span>}
                        rules={[
                          {
                            required: true,
                            message: "카테고리를 선택해주세요",
                          },
                        ]}
                        className="mb-0 w-full min-w-0"
                      >
                        <Select
                          placeholder="선택"
                          disabled={disabledFields.includes("category")}
                          className="!rounded-xl w-full max-w-full"
                          styles={{ popup: { root: { minWidth: 120 } } }}
                        >
                          {Object.entries(CATEGORY_MAPPING).map(
                            ([key, label]) => (
                              <Select.Option key={key} value={key}>
                                {label}
                              </Select.Option>
                            )
                          )}
                        </Select>
                      </Form.Item>
                    )}
                  </div>
                )}

                {(!hiddenFields.includes("recruitment_count") ||
                  (!hiddenFields.includes("start_date") &&
                    !hiddenFields.includes("end_date"))) && (
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    {!hiddenFields.includes("start_date") &&
                      !hiddenFields.includes("end_date") && (
                        <>
                          <Form.Item
                            name="start_date"
                            dependencies={["end_date"]}
                            label={
                              <span className="font-semibold">모집 시작</span>
                            }
                            rules={[
                              {
                                required: true,
                                message: "모집 시작일을 선택해주세요",
                              },
                              ({ getFieldValue }) => ({
                                validator(_, value) {
                                  const endDate = getFieldValue("end_date");
                                  if (
                                    !value ||
                                    !endDate ||
                                    value.isSame(endDate, "day") ||
                                    value.isBefore(endDate, "day")
                                  ) {
                                    return Promise.resolve();
                                  }
                                  return Promise.reject(
                                    new Error(
                                      "모집 시작일은 모집 마감일보다 늦을 수 없습니다"
                                    )
                                  );
                                },
                              }),
                            ]}
                            className="mb-0"
                          >
                            <DatePicker
                              className="w-full !rounded-xl"
                              disabledDate={(current) => {
                                const endDate = form.getFieldValue("end_date");
                                return (
                                  endDate &&
                                  current &&
                                  current.isAfter(endDate, "day")
                                );
                              }}
                              disabled={
                                disabledFields.includes("start_date") ||
                                startDatePassed
                              }
                            />
                          </Form.Item>
                          <Form.Item
                            name="end_date"
                            dependencies={["start_date"]}
                            label={
                              <span className="font-semibold">모집 마감</span>
                            }
                            rules={[
                              {
                                required: true,
                                message: "모집 마감일을 선택해주세요",
                              },
                              ({ getFieldValue }) => ({
                                validator(_, value) {
                                  const startDate = getFieldValue("start_date");
                                  if (!value || !startDate) {
                                    return Promise.resolve();
                                  }

                                  if (value.isBefore(startDate, "day")) {
                                    return Promise.reject(
                                      new Error(
                                        "모집 마감일은 모집 시작일보다 빠를 수 없습니다"
                                      )
                                    );
                                  }

                                  if (
                                    value.isAfter(
                                      startDate.add(60, "day"),
                                      "day"
                                    )
                                  ) {
                                    return Promise.reject(
                                      new Error(
                                        "모집 마감일은 모집 시작일로부터 60일 이내만 선택할 수 있습니다"
                                      )
                                    );
                                  }

                                  return Promise.resolve();
                                },
                              }),
                            ]}
                            className="mb-0"
                          >
                            <DatePicker
                              className="w-full !rounded-xl"
                              disabledDate={(current) => {
                                const startDate =
                                  form.getFieldValue("start_date");
                                if (!current || !startDate) {
                                  return false;
                                }

                                return (
                                  current.isBefore(startDate, "day") ||
                                  current.isAfter(
                                    startDate.add(60, "day"),
                                    "day"
                                  )
                                );
                              }}
                              disabled={disabledFields.includes("end_date")}
                            />
                          </Form.Item>
                          {!hiddenFields.includes("recruitment_count") && (
                            <Form.Item
                              name="recruitment_count"
                              label={
                                <span className="font-semibold whitespace-nowrap text-ellipsis overflow-hidden">
                                  모집 인원
                                  <span className="ml-1 text-base font-normal text-gray-400">
                                    (최대 100명)
                                  </span>
                                </span>
                              }
                              rules={[
                                {
                                  required: true,
                                  message: "모집인원을 입력해주세요",
                                },
                              ]}
                              className="mb-0"
                            >
                              <InputNumber
                                min={1}
                                max={100}
                                placeholder="인원 수"
                                className="w-full !rounded-xl"
                                disabled={disabledFields.includes(
                                  "recruitment_count"
                                )}
                              />
                            </Form.Item>
                          )}
                          <p className="mb-0 -mt-2 text-base text-gray-500 sm:col-span-3">
                            서비스 제공기간 모집 시작일 기준 최대 60일
                          </p>
                        </>
                      )}
                    {hiddenFields.includes("start_date") &&
                      hiddenFields.includes("end_date") &&
                      !hiddenFields.includes("recruitment_count") && (
                        <Form.Item
                          name="recruitment_count"
                          label={
                            <span className="font-semibold whitespace-nowrap text-ellipsis overflow-hidden">
                              모집 인원
                              <span className="ml-1 text-base font-normal text-gray-400">
                                (최대 100명)
                              </span>
                            </span>
                          }
                          rules={[
                            {
                              required: true,
                              message: "모집인원을 입력해주세요",
                            },
                          ]}
                          className="mb-0"
                        >
                          <InputNumber
                            min={1}
                            max={100}
                            placeholder="인원 수"
                            className="w-full !rounded-xl"
                            disabled={disabledFields.includes(
                              "recruitment_count"
                            )}
                          />
                        </Form.Item>
                      )}
                  </div>
                )}

                {!hiddenFields.includes("description") && (
                  <Form.Item
                    name="description"
                    label={
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">한 줄 소개</span>
                        <span className="text-base font-normal text-gray-400">
                          (최대 100자)
                        </span>
                      </div>
                    }
                    rules={[
                      { required: true, message: "한 줄 소개를 입력해주세요" },
                    ]}
                    className="mb-0"
                  >
                    <Input.TextArea
                      rows={2}
                      maxLength={100}
                      placeholder="캠페인을 한 줄로 요약해 주세요. 상세 내용은 아래 미션에서 적을 수 있어요."
                      disabled={disabledFields.includes("description")}
                      className="!resize-none !rounded-xl text-[15px] leading-relaxed"
                    />
                  </Form.Item>
                )}

                {!hiddenFields.includes("product_info") && (
                  <Form.Item
                    name="product_info"
                    label={
                      <span className="font-semibold">
                        제공 제품 정보
                        <span className="ml-1.5 font-normal text-gray-500">
                          (선택)
                        </span>
                      </span>
                    }
                    className="mb-0 min-w-0 sm:col-span-2"
                  >
                    <Input
                      placeholder="제공 제품 정보를 입력해주세요"
                      disabled={disabledFields.includes("product_info")}
                      className="!rounded-xl border-gray-200"
                    />
                  </Form.Item>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {(!hiddenFields.includes("thumbnail") ||
        !hiddenFields.includes("attachment")) && (
        <section id="cf-section-media" className="scroll-mt-28 mb-6 md:mb-8">
          <div className={SECTION_CARD}>
            <SectionHeader
              title="이미지"
              description="목록·상세 내용에 노출될 썸네일과 상세 페이지 이미지를 등록합니다."
            />
            <div className={PANEL}>
              <div className="flex flex-wrap gap-8">
                {!hiddenFields.includes("thumbnail") && (
                  <Form.Item
                    name="thumbnail"
                    label={
                      <div className="flex flex-col gap-1">
                        <span className="font-semibold">썸네일</span>
                        <span className="text-lg font-normal text-gray-400">
                          1200*630px | jpg, png | 30MB 이하
                        </span>
                      </div>
                    }
                    className="mb-0 min-w-[200px] flex-1"
                  >
                    <div className="flex flex-wrap items-start gap-4">
                      <Upload
                        listType="picture-card"
                        accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                        showUploadList={false}
                        beforeUpload={handleThumbnailBeforeUpload}
                        maxCount={1}
                      >
                        <div>
                          <UploadOutlined />
                          <div className="text-lg">업로드</div>
                        </div>
                      </Upload>
                      {(thumbnailFile || initialValues?.thumbnail) && (
                        <div className="relative h-[102px] w-[102px]">
                          <Image
                            src={
                              thumbnailFile
                                ? URL.createObjectURL(thumbnailFile)
                                : initialValues.thumbnail
                            }
                            alt="썸네일"
                            width={102}
                            height={102}
                            className="cursor-pointer rounded-xl border border-gray-200 object-cover"
                            preview={{ mask: false }}
                          />
                          <button
                            type="button"
                            className="absolute right-1 top-1 cursor-pointer text-gray-400 hover:text-gray-600"
                            onClick={() => {
                              setThumbnailFile(null);
                              onThumbnailChange?.(null);
                              form.setFieldsValue({ thumbnail: null });
                            }}
                          >
                            <TiDeleteOutline />
                          </button>
                        </div>
                      )}
                    </div>
                  </Form.Item>
                )}
                {!hiddenFields.includes("attachment") && (
                  <Form.Item
                    name="attachment"
                    label={
                      <div className="flex flex-col gap-1">
                        <span className="font-semibold">상세 페이지</span>
                        <span className="text-lg font-normal text-gray-400">
                          jpg, png, gif | 30MB 이하
                        </span>
                      </div>
                    }
                    className="mb-0 min-w-[200px] flex-1"
                  >
                    <div className="flex flex-wrap items-start gap-4">
                      <Upload
                        listType="picture-card"
                        accept=".jpg,.jpeg,.png,.gif,image/jpeg,image/png,image/gif"
                        showUploadList={false}
                        beforeUpload={(file) => {
                          const maxBytes = 30 * 1024 * 1024;
                          if (file.size > maxBytes) {
                            message.error("파일 크기는 30MB 이하여야 합니다.");
                            return Upload.LIST_IGNORE;
                          }
                          setAttachmentFile(file);
                          onAttachmentChange?.(file);
                          return false;
                        }}
                        maxCount={1}
                      >
                        <div>
                          <UploadOutlined />
                          <div className="text-lg">업로드</div>
                        </div>
                      </Upload>
                      {(attachmentFile || initialValues?.attachment_url) && (
                        <div className="relative h-[102px] w-[102px]">
                          <Image
                            src={
                              attachmentFile
                                ? URL.createObjectURL(attachmentFile)
                                : initialValues.attachment_url
                            }
                            alt="상세 페이지"
                            width={102}
                            height={102}
                            className="cursor-pointer rounded-xl border border-gray-200 object-cover"
                            preview={{ mask: false }}
                          />
                          <button
                            type="button"
                            className="absolute right-1 top-1 cursor-pointer text-gray-400 hover:text-gray-600"
                            onClick={() => {
                              setAttachmentFile(null);
                              onAttachmentChange?.(null);
                              form.setFieldsValue({ attachment: null });
                            }}
                          >
                            <TiDeleteOutline />
                          </button>
                        </div>
                      )}
                    </div>
                  </Form.Item>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {!hiddenFields.includes("content_format") && (
        <section id="cf-section-format" className="scroll-mt-28 mb-6 md:mb-8">
          <div className={SECTION_CARD}>
            <SectionHeader
              title="캠페인을 진행할 콘텐츠 형식을 선택하세요"
              description={
                <>
                  <p>크리에이터가 제작할 주요 채널·포맷에 맞춰 선택합니다.</p>
                  <p>
                    현재 TikTok만 지원하며, 다른 플랫폼은 곧 오픈될 예정입니다.
                  </p>
                </>
              }
            />
            <div className={PANEL}>
              <Form.Item
                name="content_format"
                rules={[
                  { required: true, message: "콘텐츠 형식을 선택해 주세요" },
                ]}
                className="mb-0"
              >
                <FormatSelector
                  disabled={disabledFields.includes("content_format")}
                  allowedValues={contentFormatAllowedValues}
                />
              </Form.Item>
            </div>
          </div>
        </section>
      )}

      {(!hiddenFields.includes("seeding_type") ||
        !hiddenFields.includes("requires_shipment") ||
        !hiddenFields.includes("allow_secondary_use")) && (
        <section id="cf-section-ops" className="scroll-mt-28 mb-6 md:mb-8">
          <div className={SECTION_CARD}>
            <SectionHeader
              title="운영 방식"
              description={
                <>
                  <p>
                    시딩 타입·배송 여부·2차 활용 여부를 선택하고, 유가
                    캠페인이면 통화와 리워드를 입력해 주세요.
                  </p>
                  <p>리워드가 300만 원을 넘는 경우에는 별도 문의해 주세요.</p>
                </>
              }
            />
            <div className={PANEL}>
              <div className="flex w-full flex-col gap-6">
                <div className="flex w-full flex-col gap-6 sm:flex-row sm:items-start sm:gap-8">
                  {!hiddenFields.includes("seeding_type") && (
                    <Form.Item
                      name="seeding_type"
                      label={
                        <span className="font-semibold whitespace-nowrap text-ellipsis overflow-hidden">
                          시딩 타입
                        </span>
                      }
                      rules={[
                        {
                          required: true,
                          message: "시딩 타입을 선택해 주세요",
                        },
                      ]}
                      className="mb-0 w-full min-w-0 sm:flex-1"
                    >
                      <BinaryRadios
                        disabled={disabledFields.includes("seeding_type")}
                        options={[
                          { value: "free", label: "무가" },
                          { value: "paid", label: "유가" },
                        ]}
                      />
                    </Form.Item>
                  )}
                  {!hiddenFields.includes("requires_shipment") && (
                    <Form.Item
                      name="requires_shipment"
                      label={
                        <span className="font-semibold whitespace-nowrap text-ellipsis overflow-hidden">
                          배송 여부
                        </span>
                      }
                      rules={[
                        {
                          required: true,
                          message: "배송 여부를 선택해 주세요",
                        },
                      ]}
                      className="mb-0 w-full min-w-0 sm:flex-1"
                    >
                      <BinaryRadios
                        disabled={disabledFields.includes("requires_shipment")}
                        options={[
                          { value: true, label: "필요" },
                          { value: false, label: "불필요" },
                        ]}
                      />
                    </Form.Item>
                  )}

                  {!hiddenFields.includes("allow_secondary_use") && (
                    <Form.Item
                      name="allow_secondary_use"
                      label={
                        <span className="font-semibold whitespace-nowrap text-ellipsis overflow-hidden">
                          2차 활용 여부
                        </span>
                      }
                      rules={[
                        {
                          required: true,
                          message: "2차 활용 여부를 선택해 주세요",
                        },
                      ]}
                      className="mb-0 w-full min-w-0 sm:flex-1"
                    >
                      <BinaryRadios
                        disabled={disabledFields.includes(
                          "allow_secondary_use"
                        )}
                        options={[
                          { value: true, label: "예" },
                          { value: false, label: "아니오" },
                        ]}
                      />
                    </Form.Item>
                  )}
                </div>
                {(!hiddenFields.includes("currency") ||
                  !hiddenFields.includes("payout")) && (
                  <div className="w-full min-w-0 sm:flex-[2]">
                    <div className="flex w-full min-w-0 gap-4">
                      {!hiddenFields.includes("payout") && (
                        <Form.Item
                          name="payout"
                          dependencies={["seeding_type"]}
                          label={
                            <span className="font-semibold whitespace-nowrap text-ellipsis overflow-hidden">
                              리워드(인당 마케팅 대행비)
                            </span>
                          }
                          rules={
                            formValues?.seeding_type === "paid"
                              ? [
                                  {
                                    validator: (_, value) => {
                                      if (
                                        value === null ||
                                        value === undefined ||
                                        value === "" ||
                                        value <= 0
                                      ) {
                                        return Promise.reject(
                                          new Error("리워드를 입력해 주세요.")
                                        );
                                      }
                                      const num = Number(value);
                                      if (num > PAYOUT_MAX) {
                                        return Promise.reject(
                                          new Error("300만 원 초과 입력 금지")
                                        );
                                      }
                                      if (num % PAYOUT_STEP !== 0) {
                                        return Promise.reject(
                                          new Error(
                                            "5만 원 단위로 선택해 주세요."
                                          )
                                        );
                                      }
                                      return Promise.resolve();
                                    },
                                  },
                                ]
                              : []
                          }
                          validateTrigger={["onChange", "onBlur"]}
                          className="mb-0 min-w-0 flex-[2]"
                        >
                          <Select
                            showSearch
                            optionFilterProp="label"
                            options={payoutOptions}
                            className="w-full max-w-full !rounded-xl"
                            placeholder="리워드를 입력해 주세요."
                            disabled={
                              disabledFields.includes("payout") ||
                              formValues?.seeding_type !== "paid"
                            }
                            filterOption={(input, option) =>
                              (option?.label ?? "")
                                .replace(/,/g, "")
                                .toLowerCase()
                                .includes(input.replace(/,/g, "").toLowerCase())
                            }
                          />
                        </Form.Item>
                      )}
                      {!hiddenFields.includes("currency") && (
                        <Form.Item
                          name="currency"
                          dependencies={["seeding_type"]}
                          label={
                            <span className="font-semibold whitespace-nowrap text-ellipsis overflow-hidden">
                              통화
                            </span>
                          }
                          rules={
                            formValues?.seeding_type === "paid"
                              ? [
                                  {
                                    required: true,
                                    message: "통화를 선택해 주세요",
                                  },
                                ]
                              : []
                          }
                          className="mb-0 min-w-0 flex-1"
                        >
                          <Select
                            placeholder="통화"
                            disabled={
                              disabledFields.includes("currency") ||
                              formValues?.seeding_type !== "paid"
                            }
                            className="!rounded-xl w-full"
                          >
                            <Select.Option value="KRW">KRW</Select.Option>
                            {/* <Select.Option value="USD">USD</Select.Option> */}
                          </Select>
                        </Form.Item>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {!hiddenFields.includes("mission") && (
        <section id="cf-section-mission" className="scroll-mt-28 mb-6 md:mb-8">
          <div className={SECTION_CARD}>
            <SectionHeader
              title="세부 내용"
              description={
                <>
                  <p>크리에이터가 수행할 캠페인 미션 내용을 등록해 주세요.</p>
                  <p>
                    해시태그·계정 태그·키워드의 경우 한 번에 여러 개를 올릴 때
                    쉼표(,)를 사용하고 입력 후 엔터로 추가해 주세요.
                  </p>
                </>
              }
            />
            <div className={PANEL}>
              <div className="flex flex-col gap-5">
                <Form.Item
                  name="mission_guide"
                  label={
                    <span className="font-semibold">
                      캠페인 안내
                      <span className="ml-1.5 font-normal text-red-600">
                        (필수)
                      </span>
                    </span>
                  }
                  rules={
                    missionDisabled
                      ? []
                      : [
                          {
                            required: true,
                            message: "캠페인 미션을 입력해 주세요",
                          },
                        ]
                  }
                  className="mb-0"
                >
                  <Input.TextArea
                    rows={8}
                    placeholder="촬영 방식, 필수 노출 포인트, 금지 표현, 업로드 유의사항 등을 자유롭게 작성해 주세요."
                    disabled={missionDisabled}
                    className="!rounded-xl text-[15px] leading-relaxed"
                  />
                </Form.Item>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:items-start">
                  <Form.Item
                    name="mission_hashtags"
                    label={
                      <span className="font-semibold">
                        필수 해시태그
                        <span className="ml-1.5 font-normal text-gray-500">
                          (선택)
                        </span>
                      </span>
                    }
                    className="mb-0"
                  >
                    <MissionTagInput
                      disabled={missionDisabled}
                      placeholder="콘텐츠에 포함해야 할 해시태그를 입력해 주세요."
                      normalizeTag={normalizeMissionHashtag}
                    />
                  </Form.Item>
                  <Form.Item
                    name="mission_account_tags"
                    label={
                      <span className="font-semibold">
                        필수 계정 태그
                        <span className="ml-1.5 font-normal text-gray-500">
                          (선택)
                        </span>
                      </span>
                    }
                    className="mb-0"
                  >
                    <MissionTagInput
                      disabled={missionDisabled}
                      placeholder="콘텐츠에 태그해야 할 계정을 입력해 주세요."
                      normalizeTag={normalizeMissionAccountTag}
                    />
                  </Form.Item>
                </div>
                <Form.Item
                  name="mission_keywords"
                  label={
                    <span className="font-semibold">
                      키워드
                      <span className="ml-1.5 font-normal text-gray-500">
                        (선택)
                      </span>
                    </span>
                  }
                  className="mb-0"
                >
                  <MissionTagInput
                    disabled={missionDisabled}
                    placeholder="콘텐츠에 포함할 키워드를 입력해 주세요."
                  />
                </Form.Item>
                <Form.List name="mission_custom_sections">
                  {(fields, { add, remove }) => (
                    <div className="flex flex-col gap-4">
                      {fields.map(({ key, name, ...restField }, index) => (
                        <div key={key}>
                          <div className="mb-4 flex items-center justify-between">
                            <p className="m-0 text-[15px] font-semibold text-gray-900">
                              추가 항목 {index + 1}
                            </p>
                            {!missionDisabled && (
                              <Button
                                size="small"
                                danger
                                type="text"
                                onClick={() => remove(name)}
                              >
                                삭제
                              </Button>
                            )}
                          </div>
                          <div className="flex flex-col gap-4 rounded-2xl border border-gray-200 bg-white p-4">
                            <Form.Item
                              {...restField}
                              name={[name, "title"]}
                              label={
                                <span className="font-semibold">제목</span>
                              }
                              validateTrigger={["onChange", "onBlur"]}
                              rules={[
                                {
                                  validator: (_, value) => {
                                    const normalized = value?.trim();

                                    if (!normalized) return Promise.resolve();

                                    if (
                                      DEFAULT_MISSION_TITLES.has(normalized)
                                    ) {
                                      return Promise.reject(
                                        new Error(
                                          "기본 미션 항목 이름은 추가 항목 제목으로 사용할 수 없습니다."
                                        )
                                      );
                                    }

                                    return Promise.resolve();
                                  },
                                },
                              ]}
                              className="mb-0"
                            >
                              <Input
                                placeholder="추가 항목의 제목을 입력해 주세요."
                                disabled={missionDisabled}
                                className="!rounded-xl border-gray-200"
                              />
                            </Form.Item>
                            <Form.Item
                              {...restField}
                              name={[name, "desc"]}
                              label={
                                <span className="font-semibold">내용</span>
                              }
                              className="mb-0"
                            >
                              <Input.TextArea
                                rows={4}
                                placeholder="추가로 안내할 내용을 입력해 주세요."
                                disabled={missionDisabled}
                                className="!resize-none !rounded-xl text-[15px] leading-relaxed"
                              />
                            </Form.Item>
                          </div>
                        </div>
                      ))}
                      {!missionDisabled && (
                        <Button
                          type="dashed"
                          onClick={() => add({ title: "", desc: "" })}
                          className="h-11 rounded-xl border-gray-300 font-semibold text-gray-700"
                        >
                          항목 추가
                        </Button>
                      )}
                    </div>
                  )}
                </Form.List>
              </div>
            </div>
          </div>
        </section>
      )}

      {mode === "create" && (
        <section id="cf-section-legal" className="scroll-mt-28 mb-6 md:mb-8">
          <div className={SECTION_CARD}>
            <SectionHeader
              title="약관 동의"
              description="원활한 캠페인 운영을 위해 아래 내용을 확인하고 동의해 주세요."
            />
            <div className={PANEL}>
              <div className="mb-12 flex flex-row flex-wrap items-baseline gap-x-8 gap-y-1 text-xl text-gray-900">
                <span className="inline-flex min-w-0 items-baseline gap-2">
                  <span className="shrink-0 font-semibold">
                    계약자 - {formValues?.username || "—"}
                  </span>
                </span>
                <span className="inline-flex min-w-0 items-baseline gap-2">
                  <span className="shrink-0 font-semibold">
                    계약 회사명 - {formValues?.company_name || "—"}
                  </span>
                </span>
              </div>
              <Form.Item name="username" hidden>
                <Input />
              </Form.Item>
              <Form.Item name="company_name" hidden>
                <Input />
              </Form.Item>
              <Form.Item name="contractData" className="mb-0">
                <Contract
                  onChange={(data) =>
                    form.setFieldsValue({ contractData: data })
                  }
                />
              </Form.Item>
            </div>
          </div>
        </section>
      )}

      {mode === "create" && (
        <div className="flex items-center justify-end">
          <Button type="primary" htmlType="submit" disabled={!isFormValid()}>
            생성하기
          </Button>
        </div>
      )}
    </Form>
  );

  const thumbnailCropper = (
    <Router.ImageCropper
      open={cropModalOpen}
      imageUrl={cropImageUrl}
      originalFile={pendingThumbnailFile}
      aspect={THUMBNAIL_ASPECT}
      outputWidth={THUMBNAIL_WIDTH}
      outputHeight={THUMBNAIL_HEIGHT}
      title="썸네일 자르기"
      helperText="업로드한 이미지를 썸네일 비율에 맞게 잘라주세요."
      onCancel={resetCropState}
      onConfirm={(croppedFile) => {
        setThumbnailFile(croppedFile);
        onThumbnailChange?.(croppedFile);
        resetCropState();
      }}
    />
  );

  if (!showSidebar) {
    return (
      <div className="flex w-full flex-col gap-6 md:gap-8">
        {formInner}
        {thumbnailCropper}
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-8 lg:flex-row lg:items-start lg:gap-10">
      <div className="flex min-w-0 flex-1 flex-col gap-6 md:gap-8 lg:max-w-[800px]">
        {formInner}
        {thumbnailCropper}
      </div>

      <aside className="w-full shrink-0 lg:sticky lg:top-6 lg:w-[300px]">
        <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="mb-4 border-b border-gray-100 pb-3">
            <p className="m-1 text-2xl font-bold text-gray-900">단계별 입력</p>
          </div>
          <nav className="flex flex-col gap-2" aria-label="캠페인 폼 단계">
            {navItems.map((item) => {
              const isActive = activeNav === item.id;
              const isComplete = sectionStatuses[item.id];
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => {
                    setActiveNav(item.id);
                    scrollToId(item.id);
                  }}
                  className={[
                    "w-full rounded-xl border px-3 py-2.5 text-left transition-colors",
                    isActive
                      ? "bg-gray-50/80"
                      : "border-gray-100 bg-gray-50/80 text-gray-700 hover:bg-gray-100",
                  ].join(" ")}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-xl font-semibold">{item.title}</div>
                    <span
                      className={[
                        "shrink-0 rounded-full px-2 py-0.5 text-base font-semibold",
                        isComplete
                          ? "bg-emerald-100 text-emerald-700"
                          : "bg-amber-100 text-amber-700",
                      ].join(" ")}
                    >
                      {isComplete ? "완료" : "미완료"}
                    </span>
                  </div>
                  <div
                    className={[
                      "mt-0.5 text-lg",
                      isActive ? "" : "text-gray-500",
                    ].join(" ")}
                  >
                    {item.sub}
                  </div>
                </button>
              );
            })}
          </nav>
        </div>
      </aside>
    </div>
  );
};
